import { memo, ReactNode } from "react";
import { useTranslations } from "next-intl";
import { Card, CardContent } from "@/components/ui/card";

export type StateType = 'loading' | 'empty' | 'error';

interface StateCardProps {
  /**
   * Тип состояния
   */
  type: StateType;
  
  /**
   * Кастомное сообщение (опционально)
   */
  message?: string;
  
  /**
   * Кастомная иконка (опционально)
   */
  icon?: ReactNode;
}

/**
 * Универсальный компонент для отображения состояний
 * Заменяет LoadingState и EmptyState из AIChartsPanel/ui
 * 
 * @example
 * ```tsx
 * // Загрузка
 * <StateCard type="loading" />
 * 
 * // Пустое состояние
 * <StateCard type="empty" message="Нет данных" />
 * 
 * // Ошибка
 * <StateCard type="error" message="Ошибка загрузки" />
 * ```
 */
export const StateCard = memo(function StateCard({ 
  type, 
  message,
  icon 
}: StateCardProps) {
  const t = useTranslations();
  
  // Определяем сообщение по умолчанию
  const defaultMessage = {
    loading: t("label-report-loading"),
    empty: t("label-report-empty"),
    error: t("label-ai-report-error"),
  }[type];
  
  const displayMessage = message || defaultMessage;
  
  // Добавляем анимацию для loading
  const textClass = type === 'loading' 
    ? 'animate-pulse text-muted-foreground' 
    : 'text-muted-foreground';
  
  return (
    <Card className="w-full bg-white flex-1 flex flex-col min-h-0">
      <CardContent className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          {icon && <div className="mb-2">{icon}</div>}
          <div className={textClass}>
            {displayMessage}
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

export default StateCard;
